/* Engineering delivery for the AIX company: PRs merged this week vs last, PRs aging open, review and
   ready-to-merge wait. Ingest health is its own row, because a partial or stale ingest makes every number low. */
import type { inferRouterOutputs } from "@trpc/server"
import { useCallback } from "react"

import type { AppRouter } from "../../../../../aixinternal-api/src/router/_app"
import { trpc } from "../../../lib/trpc"
import { ago, worstVerdict } from "../homeDashboardModel"
import { EVerdict } from "../homeDashboard.types"
import type { IWidgetDefinition, IWidgetResult, IWidgetRow } from "../homeDashboard.types"
import { KeyValues } from "./WidgetParts"
import { WIDGET_DAILY_QUERY_OPTIONS, useQueryWidget } from "./widgetQuery"

type TOutputs = inferRouterOutputs<AppRouter>["engineeringDelivery"]
type TSummary = TOutputs["summary"]
type TSourceStatus = TOutputs["status"][number]
type TDistribution = TSummary["readyToMerge"]

/** The only engineering source is the AIX monorepo; its PRs can't be split by product, so rows belong to AIX */
const COMPANY_SLUG = "aix"
const HOUR_MS = 3_600_000
const WEEK_MS = 7 * 24 * HOUR_MS
/** A complete ingest older than this means the numbers are behind */
const STALE_INGEST_HOURS = 48
/** Merged count falling below this share of last week is worth a look */
const MERGED_DROP_RATIO = 0.5

const hours = (value: number | null) => {
  if (value === null) return "—"
  return value < 48 ? `${value.toFixed(1)}h` : `${(value / 24).toFixed(1)}d`
}

const distribution = (d: TDistribution) =>
  d.sampleCount ? `median ${hours(d.medianHours)} · p90 ${hours(d.p90Hours)} (${d.sampleCount} PRs)` : "no samples"

const ingestRow = (sources: TSourceStatus[], summary: TSummary, now: number): IWidgetRow => {
  const latest = sources.map((s) => s.latestRun).find(Boolean)
  const complete = sources.map((s) => s.latestCompleteRun).find(Boolean)
  const completeAge = complete ? now - new Date(complete.startedAt).getTime() : Infinity
  const healthy = summary.completeness === "complete" && completeAge < STALE_INGEST_HOURS * HOUR_MS
  const verdict = healthy ? EVerdict.CLEAR : EVerdict.WATCH
  return {
    key: "ingest",
    company: COMPANY_SLUG,
    verdict,
    pill: { text: healthy ? "fresh" : summary.completeness, verdict },
    title: "GitHub ingest",
    sub: healthy
      ? `${sources.length} repo${sources.length === 1 ? "" : "s"} · last complete ${ago(complete?.startedAt, now)}`
      : `Numbers may be low · last complete run ${ago(complete?.startedAt, now)}`,
    meta: latest ? `${latest.status} ${ago(latest.startedAt, now)}` : "never ran",
    quiet: healthy,
    details: (
      <KeyValues
        pairs={[
          ["Repos", sources.map((s) => `${s.source.repositoryOwner}/${s.source.repositoryName}`).join(", ")],
          ["Latest run", latest ? `${latest.status} · ${ago(latest.startedAt, now)}` : null],
          ["Gaps", summary.unavailableReasons.slice(0, 3).join(" · ") || null],
        ]}
      />
    ),
  }
}

const renderDelivery = (summary: TSummary, previous: TSummary | undefined, sources: TSourceStatus[]): IWidgetResult => {
  const now = Date.now()
  const merged = summary.population.merged
  const lastWeek = previous?.population.merged
  const mergedDropped = lastWeek !== undefined && lastWeek > 0 && merged < lastWeek * MERGED_DROP_RATIO
  const aging = summary.openAgeBuckets
  const stuck = aging.overSevenDays
  const slow = aging.threeToSevenDays

  const rows: IWidgetRow[] = [
    ingestRow(sources, summary, now),
    {
      key: "merged",
      company: COMPANY_SLUG,
      verdict: mergedDropped ? EVerdict.WATCH : EVerdict.CLEAR,
      title: `${merged} PRs merged this week`,
      sub: lastWeek === undefined ? "Last week loading" : `${lastWeek} the week before`,
      meta: `${summary.population.open} open`,
    },
    {
      key: "stuck",
      company: COMPANY_SLUG,
      verdict: stuck ? EVerdict.WATCH : EVerdict.CLEAR,
      pill: { text: `${stuck}`, verdict: stuck ? EVerdict.WATCH : EVerdict.CLEAR },
      title: "PRs open over 7 days",
      sub: `${slow} at 3–7 days · ${aging.oneToThreeDays} at 1–3 days · ${aging.under24h} under a day`,
      quiet: !stuck,
      href: "https://github.com/AIxCo/aix-turborepo/pulls?q=is%3Apr+is%3Aopen+sort%3Acreated-asc",
      hrefLabel: "Oldest PRs",
    },
    {
      key: "review",
      company: COMPANY_SLUG,
      verdict: EVerdict.CLEAR,
      title: "First human review",
      sub: distribution(summary.initialHumanReviewWait),
      quiet: true,
    },
    {
      key: "ready",
      company: COMPANY_SLUG,
      verdict: EVerdict.CLEAR,
      title: "Ready to merge",
      sub: distribution(summary.readyToMerge),
      quiet: true,
    },
  ]

  return {
    verdict: worstVerdict(rows.map((r) => r.verdict)),
    count: `${merged} merged 7d`,
    rows,
    ordered: true,
    quietLabel: "Review timing",
    foot: summary.completeness === "complete" ? undefined : "Ingest is incomplete; counts are a floor.",
  }
}

/** Hour-aligned windows so query keys change once an hour, not every render */
const weekWindow = (weeksBack: number) => {
  const end = Math.floor(Date.now() / HOUR_MS) * HOUR_MS - weeksBack * WEEK_MS
  return { windowStart: new Date(end - WEEK_MS).toISOString(), windowEnd: new Date(end).toISOString() }
}

export const engDeliveryWidget: IWidgetDefinition = {
  id: "eng-delivery",
  title: "Eng delivery",
  description: "PRs merged vs last week, PRs aging open, review wait",
  columns: { 2: 2, 3: 3, 4: 4, 5: 5, 6: 6 },
  useWidget: () => {
    const company = trpc.companies.getBySlug.useQuery({ slug: COMPANY_SLUG }, { staleTime: Infinity })
    const companyId = company.data?.id ?? ""
    const enabled = { ...WIDGET_DAILY_QUERY_OPTIONS, enabled: !!companyId }
    const status = trpc.engineeringDelivery.status.useQuery({ companyId }, enabled)
    const previous = trpc.engineeringDelivery.summary.useQuery({ companyId, ...weekWindow(1) }, enabled)
    const current = trpc.engineeringDelivery.summary.useQuery({ companyId, ...weekWindow(0) }, enabled)
    const render = useCallback(
      (summary: TSummary) => renderDelivery(summary, previous.data, status.data ?? []),
      [previous.data, status.data],
    )
    const state = useQueryWidget(current, render)
    if (company.error) return { ...state, error: company.error.message, isLoading: false }
    return { ...state, isLoading: state.isLoading || company.isLoading }
  },
}
